import { supabase } from '@/lib/supabase';
import { appLogger } from '@/lib/logger';
import { captureApplicationError } from '@/lib/monitoring';

export type AuthRateLimitAction =
  | 'signIn'
  | 'signUp'
  | 'resetPassword'
  | 'resendConfirmation';

interface AuthRateLimitResponse {
  ok?: boolean;
  allowed?: boolean;
  retryAfterSeconds?: number;
  reason?: string | null;
}

export async function assertServerAuthRateLimit(
  action: AuthRateLimitAction,
  email?: string,
) {
  try {
    const { data, error } = await supabase.functions.invoke<AuthRateLimitResponse>(
      'auth-rate-limit',
      {
        body: { action, email },
      },
    );

    if (error) {
      captureApplicationError(error, {
        tags: { area: 'auth', operation: 'rate_limit_check', action },
      });
      appLogger.warn('Auth rate-limit check failed open', error);
      return;
    }

    if (data?.allowed === false) {
      throw new Error(buildAuthRateLimitMessage(action, data.retryAfterSeconds ?? 0));
    }
  } catch (error) {
    if (error instanceof Error && error.message.startsWith('Too many')) {
      throw error;
    }

    captureApplicationError(error, {
      tags: { area: 'auth', operation: 'rate_limit_check_exception', action },
    });
    appLogger.warn('Auth rate-limit check failed open', error);
  }
}

/**
 * Whether a failure was the rate limiter rather than the credentials. That distinction
 * has to reach the person: being told to check a password that is correct sends them to
 * reset it, which does not help and wastes the wait.
 */
export function isAuthRateLimitError(error: unknown): boolean {
  return error instanceof Error && error.message.startsWith('Too many');
}

export function buildAuthRateLimitMessage(
  action: AuthRateLimitAction,
  retryAfterSeconds: number,
) {
  return `Too many ${authRateLimitActionLabel(action)} attempts. Try again in ${formatRetryAfter(retryAfterSeconds)}.`;
}

function authRateLimitActionLabel(action: AuthRateLimitAction) {
  switch (action) {
    case 'signUp':
      return 'sign-up';
    case 'resetPassword':
      return 'password reset';
    case 'resendConfirmation':
      return 'confirmation email';
    case 'signIn':
    default:
      return 'sign-in';
  }
}

function formatRetryAfter(seconds: number) {
  const safeSeconds = Math.max(1, Math.ceil(seconds));
  if (safeSeconds < 60) {
    return `${safeSeconds}s`;
  }

  const minutes = Math.ceil(safeSeconds / 60);
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.ceil(minutes / 60);
  return `${hours}h`;
}
